import { useCallback, useEffect, useRef, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";

import { http } from "@/utils/http";

import { Message, Thread } from "../types/chatbotTypes";

interface QuestionData {
  id?: number;
  answer?: string;
  ended_at?: string;
  failed_at?: string;
  question: string;
  sources?: any;
  started_at?: string;
}

interface UseChatMessagesProps {
  documentVersionId?: null | number;
  selectedText?: string;
  threadId?: null | number | string;
  onThreadUpdate?: (thread: Thread) => void;
}

interface UseChatMessagesReturn {
  isLoadingMessages: boolean;
  isLoadingSend: boolean;
  messages: Message[];
  handleDismissMessage: (messageId: number) => void;
  handleRetryMessage: (messageId: number) => Promise<void>;
  handleTypingComplete: (messageId: number) => void;
  sendMessage: (text: string) => Promise<void>;
}

/**
 * Custom hook for managing chat messages state and operations
 */
export const useChatMessages = ({
  documentVersionId,
  onThreadUpdate,
  selectedText,
  threadId,
}: UseChatMessagesProps): UseChatMessagesReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const loadingMessageId = useRef<number>(0);
  const typingMessageIds = useRef<Set<number>>(new Set());

  // Fetch messages using TanStack React Query
  const { data: questionsData, isLoading: isLoadingMessages } = useQuery({
    enabled: !!threadId,
    queryKey: ["chatbot-messages", threadId],
    queryFn: async (): Promise<QuestionData[]> => {
      if (!threadId) {
        return [];
      }

      const endpoint = `/api/chatbot/messages/${threadId}`;
      const response = await http.get<QuestionData[]>(endpoint);
      return response.data || [];
    },
  });

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
    }
  }, [threadId]);

  // Convert API response to Message format
  useEffect(() => {
    if (questionsData) {
      const chatMessages: Message[] = questionsData
        .reverse()
        .flatMap((question) => {
          const messageArray: Message[] = [];

          // Add user question
          messageArray.push({
            id: question.id || Date.now(),
            content: question.question,
            type: "user",
          });

          // Add assistant response if available
          if (question.answer) {
            const assistantId = (question.id || Date.now()) + 1000;
            messageArray.push({
              id: assistantId,
              content: question.answer,
              sources: question.sources || [],
              type: "assistant",
              // Preserve typing state if this message is currently typing
              typing: typingMessageIds.current.has(assistantId),
            });
          } else if (question.failed_at) {
            messageArray.push({
              id: (question.id || Date.now()) + 1000,
              content: "",
              error: "Failed to generate response",
              type: "error",
            });
          } else if (question.started_at && !question.ended_at) {
            messageArray.push({
              id: (question.id || Date.now()) + 1000,
              content: "",
              type: "loading",
            });
          }

          return messageArray;
        });

      setMessages(chatMessages);
    } else if (threadId && questionsData !== undefined) {
      // If we have a threadId but no data, ensure messages are cleared
      setMessages([]);
    }
  }, [questionsData, threadId]);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (userContent: string): Promise<any> => {
      const endpoint = `/api/chatbot/messages`;
      const payload: Record<string, any> = {
        documentVersionId: documentVersionId || undefined,
        fast: true,
        text: userContent,
        // TODO: Add support for context. Wait for backend to support it
        // selection || context: selectedText || undefined,
      };
      if (threadId) {
        payload.thread = threadId;
      }
      if (selectedText && selectedText.trim().length > 0) {
        payload.selectedText = selectedText;
      }
      const response = await http.post(endpoint, payload);
      return response.data;
    },
    onError: (error: any) => {
      let errorMessage = "An unexpected error occurred";

      if (error?.response?.data?.error === "too_many_documents") {
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.type === "loading" && msg.id === loadingMessageId.current) {
            return {
              id: Date.now(),
              content: "",
              error: errorMessage,
              type: "error" as const,
            };
          }
          return msg;
        })
      );
    },
    onSuccess: (data) => {
      const assistantId = (data.id || Date.now()) + 1000;
      // Track this message as typing
      typingMessageIds.current.add(assistantId);

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.type === "loading" && msg.id === loadingMessageId.current) {
            return {
              ...data,
              id: assistantId,
              content: data.answer || "",
              type: "assistant" as const,
              typing: true,
            };
          }
          return msg;
        })
      );
      // Update thread for new threads or when thread_title is provided
      if (data.thread_uuid && onThreadUpdate) {
        const thread: Thread = {
          id: data.id,
          created_at: new Date().toISOString(),
          project_id: null,
          title: data.thread_title || "New Chat",
          updated_at: new Date().toISOString(),
          user_id: 0,
          uuid: data.thread_uuid,
        };
        onThreadUpdate(thread);
      }
    },
  });

  const sendMessage = useCallback(
    async (text: string): Promise<void> => {
      if (!text.trim() || sendMessageMutation.isPending) return;

      const userMessage: Message = {
        id: Date.now(),
        content: text.trim(),
        type: "user",
      };

      const loadingMessage: Message = {
        id: Date.now() + 1,
        content: "",
        type: "loading",
      };

      loadingMessageId.current = loadingMessage.id;

      setMessages((prev) => [...prev, userMessage, loadingMessage]);

      // Send the message using the mutation
      sendMessageMutation.mutate(userMessage.content);
    },
    [sendMessageMutation]
  );

  // Message handler functions
  const handleTypingComplete = useCallback((messageId: number): void => {
    // Remove from typing set
    typingMessageIds.current.delete(messageId);

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ? { ...msg, typing: false } : msg
      )
    );
  }, []);

  const handleDismissMessage = useCallback((messageId: number): void => {
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  }, []);

  const handleRetryMessage = useCallback(
    async (messageId: number): Promise<void> => {
      const errorMessage = messages.find((msg) => msg.id === messageId);
      if (!errorMessage) return;

      const loadingMessage: Message = {
        id: Date.now(),
        content: "",
        type: "loading",
      };

      loadingMessageId.current = loadingMessage.id;

      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? loadingMessage : msg))
      );

      const userMessageIndex =
        messages.findIndex((msg) => msg.id === messageId) - 1;
      const userMessage = messages[userMessageIndex];

      if (userMessage && userMessage.content) {
        sendMessageMutation.mutate(userMessage.content);
      }
    },
    [messages, sendMessageMutation]
  );

  return {
    handleDismissMessage,
    handleRetryMessage,
    handleTypingComplete,
    isLoadingMessages,
    isLoadingSend: sendMessageMutation.isPending,
    messages,
    sendMessage,
  };
};
